/**
 * RowActions · PMS.V3 standard row "Actions ▾" dropdown (B0 · Jun 2026)
 * Mirrors the sealed Sales Order Register Actions dropdown EXACTLY (trigger button,
 * DropdownMenuContent align/style, empty-guard "—", semantic item colours).
 *
 * Presentation/interaction layer ONLY. Every handler, permission, status/lifecycle
 * check, confirmation and reason dialog stays in the caller — this component just
 * renders them inside the dropdown as an intentional safety layer against accidental
 * one-click execution of sensitive actions (Owner correction, Jun 2026).
 *
 * items: array of descriptors
 *   { key?, label, onClick, testid, tone?, disabled?, className?, show? }  // action
 *   { separator: true }                                                    // divider
 * tone → colour: submit=sky · approve=emerald · reverse=rose · danger=red · warn=amber
 */
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const TONE = {
  submit: "text-sky-300",
  approve: "text-emerald-300",
  reverse: "text-rose-300",
  danger: "text-red-400",
  warn: "text-amber-300",
};

export function RowActions({
  testid,
  items = [],
  align = "end",
  menuClassName = "min-w-[180px]",
  label = "Actions ▾",
  triggerClassName,
}) {
  const visible = (items || []).filter((it) => it && it.show !== false);
  const hasAction = visible.some((it) => !it.separator);
  if (!hasAction) return <span className="text-zinc-600">—</span>;
  return (
    <div className="flex justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            data-testid={testid}
            className={
              triggerClassName ||
              "h-7 px-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10 whitespace-nowrap"
            }
          >
            {label}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align={align}
          className={`bg-[var(--bg-1)] border-[var(--border)] text-sm ${menuClassName}`}
        >
          {visible.map((it, idx) =>
            it.separator ? (
              <DropdownMenuSeparator key={`sep-${idx}`} />
            ) : (
              <DropdownMenuItem
                key={it.key || it.testid || idx}
                data-testid={it.testid}
                disabled={it.disabled}
                className={it.className || TONE[it.tone] || ""}
                onClick={it.onClick}
              >
                {it.label}
              </DropdownMenuItem>
            )
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export default RowActions;
